import { useQuery } from '@tanstack/react-query';
import type { Grade } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

export interface OverviewStats {
  cycleLabel: string;
  currentQuarter: string | null;
  employees: number;
  needsOnboarding: number;
  goalsAccepted: number;
  goalsPending: number;
  assessmentsTotal: number;
  assessmentsCompleted: number;
  awaitingManager: number;
  awaitingHr: number;
  awaitingManagement: number;
  overdueManagerReviews: number;
  averageRating: number | null;
}

export interface DistributionBand {
  band: string;
  label: string;
  count: number;
}

export interface DepartmentStat {
  departmentId: string;
  departmentName: string;
  employees: number;
  rated: number;
  averageRating: number | null;
}

export interface NineBoxCell {
  performance: 'LOW' | 'MEDIUM' | 'HIGH';
  potential: 'LOW' | 'MEDIUM' | 'HIGH';
  label: string;
  employees: Array<{
    id: string;
    name: string;
    rating: number;
    /** Management's potential score, or null when the rating trend placed them. */
    potentialScore: number | null;
    potentialBasis: 'SCORED' | 'TREND';
  }>;
}

export interface Performer {
  employeeId: string;
  name: string;
  departmentName: string | null;
  rating: number;
  grade: Grade;
}

export interface TeamRow {
  employeeId: string;
  name: string;
  goalsAccepted: boolean;
  assessmentStatus: string | null;
  rating: number | null;
  overdue: boolean;
}

export interface ManagerStat {
  managerId: string;
  managerName: string;
  reports: number;
  rated: number;
  averageRating: number | null;
  overdueReviews: number;
}

export interface GoalCompletion {
  ratedKras: number;
  totalKras: number;
  completionPct: number;
  goalsAccepted: number;
  goalsTotal: number;
}

export interface AttritionRisk {
  employeeId: string;
  name: string;
  departmentName: string | null;
  /** 0-10, deliberately not a probability. Reasons carry the meaning. */
  score: number;
  reasons: string[];
}

export interface RecognitionSuggestion {
  employeeId: string;
  name: string;
  rating: number | null;
  daysSinceRecognition: number | null;
  reason: string;
}

const q = <T,>(key: string, path: string, enabled = true) =>
  ({ queryKey: ['analytics', key], queryFn: () => api.get<T>(path), staleTime: 60_000, enabled }) as const;

export const useOverview = (enabled = true) =>
  useQuery<OverviewStats, ApiError>(q<OverviewStats>('overview', '/analytics/overview', enabled));

export const useDistribution = (enabled = true) =>
  useQuery<DistributionBand[], ApiError>(
    q<DistributionBand[]>('distribution', '/analytics/distribution', enabled),
  );

export const useDepartmentStats = (enabled = true) =>
  useQuery<DepartmentStat[], ApiError>(
    q<DepartmentStat[]>('departments', '/analytics/departments', enabled),
  );

export const useNineBox = (enabled = true) =>
  useQuery<NineBoxCell[], ApiError>(q<NineBoxCell[]>('nine-box', '/analytics/nine-box', enabled));

export const usePerformers = (enabled = true) =>
  useQuery<{ top: Performer[]; bottom: Performer[] }, ApiError>(
    q<{ top: Performer[]; bottom: Performer[] }>('performers', '/analytics/performers', enabled),
  );

export const useMyTeam = (enabled = true) =>
  useQuery<TeamRow[], ApiError>(q<TeamRow[]>('my-team', '/analytics/my-team', enabled));

export const useManagerStats = (enabled = true) =>
  useQuery<ManagerStat[], ApiError>(q<ManagerStat[]>('managers', '/analytics/managers', enabled));

export const useGoalCompletion = (enabled = true) =>
  useQuery<GoalCompletion, ApiError>(
    q<GoalCompletion>('goal-completion', '/analytics/goal-completion', enabled),
  );

export const useAttritionRisk = (enabled = true) =>
  useQuery<AttritionRisk[], ApiError>(
    q<AttritionRisk[]>('attrition-risk', '/analytics/attrition-risk', enabled),
  );

export const useRecognitionSuggestions = (enabled = true) =>
  useQuery<RecognitionSuggestion[], ApiError>(
    q<RecognitionSuggestion[]>(
      'recognition-suggestions',
      '/analytics/recognition-suggestions',
      enabled,
    ),
  );
